/**
 * Draft-image removal reconciliation (B-006).
 *
 * Removing one draft image must never revert a *different* image selection that
 * the operator made while the removal was in flight. The earlier repair pruned
 * against the latest selling intents but still used the gallery snapshot taken
 * when removal started, so an image uploaded and assigned during the detach was
 * not in that set and its selection was cleared back to "inherit".
 *
 * The rule encoded here:
 *   - the post-delete gallery reload is authoritative when it succeeds, so an
 *     asset that is genuinely gone may be pruned;
 *   - a failed / incomplete reload is NOT evidence of deletion, so nothing is
 *     pruned on that basis — only the asset we positively detached is treated
 *     as gone;
 *   - the latest committed gallery at commit time is always unioned in, because
 *     a concurrent upload is a valid asset, never a deletion.
 *
 * This module is pure: it performs no I/O and is safe to unit test.
 */

function normalizeId(value: string | null | undefined): string | null {
  const trimmed = typeof value === "string" ? value.trim() : "";
  return trimmed.length > 0 ? trimmed : null;
}

export interface DraftMediaRemovalEvidence {
  /** Asset on the draft-media row that was detached. Null when it could not be resolved. */
  removedAssetId: string | null;
  /** Asset ids in the gallery when the removal started. */
  galleryAtStart: string[];
  /**
   * Asset ids from the authoritative post-delete reload of the draft media rows.
   * Null means the reload did not complete — absence is then not evidence.
   */
  reloadedAssetIds: string[] | null;
  /** Latest committed gallery asset ids at commit time (may include concurrent uploads). */
  liveAssetIds: string[];
  /**
   * Assets whose upload has finished but whose attach/hydrate has not committed
   * to the gallery yet. They are valid additions, never deletions.
   */
  pendingAssetIds?: string[];
  /**
   * Uploads that are still running and therefore have no asset id yet. While
   * any exist, absence from the gallery cannot be read as deletion at all.
   */
  pendingUploadCount?: number;
}

export interface DraftMediaRemovalOutcome {
  /** Assets whose channel selections must survive. */
  keepAssetIds: string[];
  /** True when the gallery was re-read after the detach, so absence is real evidence. */
  authoritative: boolean;
  /** True when pruning was deliberately skipped because an upload is unresolved. */
  deferred: boolean;
}

export function reconcileDraftMediaRemoval(
  evidence: DraftMediaRemovalEvidence,
): DraftMediaRemovalOutcome {
  const removed = normalizeId(evidence.removedAssetId);
  const live = new Set<string>();
  for (const id of evidence.liveAssetIds) {
    const normalized = normalizeId(id);
    if (normalized) live.add(normalized);
  }
  const pending = new Set<string>();
  for (const id of evidence.pendingAssetIds ?? []) {
    const normalized = normalizeId(id);
    if (normalized) pending.add(normalized);
  }
  const galleryAtStart = new Set<string>();
  for (const id of evidence.galleryAtStart) {
    const normalized = normalizeId(id);
    if (normalized) galleryAtStart.add(normalized);
  }
  const pendingUploads = Math.max(0, Math.trunc(evidence.pendingUploadCount ?? 0));

  // An upload whose asset id is not known yet could be the exact asset this
  // reconciliation would call "missing". Nothing may be pruned until it lands —
  // the upload's own hydrate prunes authoritatively.
  if (pendingUploads > 0) {
    const keepDeferred = new Set<string>([...galleryAtStart, ...live, ...pending]);
    for (const id of evidence.reloadedAssetIds ?? []) {
      const normalized = normalizeId(id);
      if (normalized) keepDeferred.add(normalized);
    }
    if (removed) keepDeferred.delete(removed);
    return { keepAssetIds: [...keepDeferred], authoritative: false, deferred: true };
  }

  const keep = new Set<string>();
  const authoritative = evidence.reloadedAssetIds !== null;

  if (authoritative) {
    for (const id of evidence.reloadedAssetIds ?? []) {
      const normalized = normalizeId(id);
      if (normalized) keep.add(normalized);
    }
    // Only additions made AFTER the removal started are unioned in. Membership
    // in the pre-removal gallery is not proof an asset still exists: a sibling
    // removal in flight would otherwise resurrect the asset it just deleted.
    for (const id of [...live, ...pending]) {
      if (!galleryAtStart.has(id)) keep.add(id);
    }
  } else {
    // No authoritative reload: keep the pre-removal gallery (minus the asset we
    // detached) plus anything newer. Never infer deletion from a failed load.
    for (const id of galleryAtStart) keep.add(id);
    for (const id of live) keep.add(id);
    for (const id of pending) keep.add(id);
  }

  // The detached asset is gone by positive evidence, regardless of load state.
  if (removed) keep.delete(removed);

  return { keepAssetIds: [...keep], authoritative, deferred: false };
}

/** Operator-facing warning when the gallery could not be re-checked. Never claims success. */
export function draftMediaRemovalWarning(outcome: DraftMediaRemovalOutcome): string | null {
  if (outcome.deferred || outcome.authoritative) return null;
  return "The image was removed, but the remaining images could not be re-checked. Your channel image selections were kept — reload the draft to verify them.";
}

/** A failed detach must never read as a successful removal. */
export function draftMediaRemovalFailure(error: unknown): string {
  const message = error instanceof Error && error.message ? error.message.trim() : "";
  return message || "Could not remove the image. Nothing was changed.";
}